import React, { useState, useEffect, useRef } from 'react';
import { db, auth } from '../../../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import './descriptionTemplate.css';

// Default description for new Creator Studio uploads (read by userEditBumpSection)
// Bump AI only generates timestamps now; these older variables still work but come out blank on new uploads.
const LEGACY_VARIABLES = /\{\{VIDEO_(DESCRIPTION|TAKEAWAYS|HASHTAGS)\}\}/;

const DescriptionTemplate = () => {
  const user = auth.currentUser;
  const [description, setDescription] = useState('');
  const [savedDescription, setSavedDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [descriptionSaved, setDescriptionSaved] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    getDoc(doc(db, 'users', user.uid, 'userSettings', 'profile'))
      .then(snap => {
        const template = snap.data()?.userTemplateDescription || '';
        setDescription(template);
        setSavedDescription(template);
      })
      .catch(() => {});
  }, [user]);

  const saveDescriptionTemplate = async () => {
    setIsSaving(true);
    try {
      await setDoc(doc(db, 'users', user.uid, 'userSettings', 'profile'), { userTemplateDescription: description }, { merge: true });
      setSavedDescription(description);
      setDescriptionSaved(true);
      setTimeout(() => setDescriptionSaved(false), 3000);
    } catch (error) {
      console.error('Error saving template:', error);
    }
    setIsSaving(false);
  };

  const handleDescriptionPaste = (text) => {
    const textarea = textareaRef.current;
    const startPos = textarea.selectionStart;
    const endPos = textarea.selectionEnd;
    const beforeValue = description.substring(0, startPos);
    const afterValue = description.substring(endPos);
    setDescription(`${beforeValue}${text}${afterValue}`);
    setTimeout(() => {
      textarea.setSelectionRange(startPos + text.length, startPos + text.length);
      textarea.focus();
    }, 0);
  };

  return (
    <>
      {/* Global header */}
      <div className="setting-header">Upload</div>

      {/* Main container for settings */}
      <div className="account-settings-container">
        {/* YouTube Description Section */}
        <div className="settings-row">
          <div className="settings-label">YouTube Description
          <div className="settings-subtitle">
            This is a template for new video uploads.
            </div>
          </div>

          <div className="settings-field">
            <button
              className="ui-setting-button ui-setting-button--primary"
              onClick={saveDescriptionTemplate}
              disabled={!description.trim() || isSaving || description === savedDescription}
            >
              {descriptionSaved ? 'Template Saved' : 'Save'}
            </button>
          </div>
        </div>
        
        <div className="description-wrapper">
          <textarea
            ref={textareaRef}
            className="description-textarea"
            placeholder="Write the description you use on every video. Add {{VIDEO_TIMESTAMPS}} where the chapters Bump AI generates should go."
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        </div>
        
        <div className="description-button-group">
          <span className="description-insert-label">Insert</span>
          <button className="ui-setting-button" onClick={() => handleDescriptionPaste('{{VIDEO_TIMESTAMPS}}')}>
            Timestamps
          </button>
        </div>

        {LEGACY_VARIABLES.test(description) && (
          <p className="description-legacy-note">
            Bump AI now generates timestamps only, so <code>{'{{VIDEO_DESCRIPTION}}'}</code>, <code>{'{{VIDEO_TAKEAWAYS}}'}</code> and{' '}
            <code>{'{{VIDEO_HASHTAGS}}'}</code> are left blank on new uploads. Replace them with your own text.{' '}
            <a href="/help/articles/9742235-what-are-the-bump-ai-variables">Learn more</a>
          </p>
        )}
      </div>
    </>
  );
};

export default DescriptionTemplate;
